import { useId } from 'react'
import type { ReactElement, ReactNode } from 'react'

/**
 * The label / hint / error layout, and the a11y wiring that goes with it.
 *
 * A render prop rather than a context or a cloned child. Both of those
 * work by reaching into a component the caller wrote, which means the
 * wiring is invisible at the call site and silently absent the moment
 * someone wraps the input in a `<div>`. Here the ids are handed over
 * explicitly and spreading them is the whole contract:
 *
 *   <Field label="Node path" hint="Absolute, e.g. /instance">
 *     {(control) => <Input {...control} value={path} onChange={...} />}
 *   </Field>
 *
 * Checkbox is the one control that does NOT use this — its label belongs
 * beside the box, not above it, so it owns its own.
 */

export interface FieldControlProps {
  id: string
  /** Points at the hint, the error, or both. `undefined` rather than an
   * empty string: `aria-describedby=""` makes some screen readers
   * announce an empty description. */
  'aria-describedby': string | undefined
  /** Only ever `true` or absent. `aria-invalid="false"` is legal but noisy
   * in the accessibility tree, and the control's border reads this. */
  'aria-invalid': true | undefined
  /** The native attribute, which is what assistive tech announces — the
   * asterisk `Field` draws is decorative. */
  required: true | undefined
}

export interface FieldProps {
  label: ReactNode
  /** Static help — a format, a constraint, where the value comes from.
   * Stays visible alongside an error; the two answer different questions. */
  hint?: ReactNode
  /** Validation failure. `null` and `''` both mean "no error", because
   * form state in this app stores an empty string for a cleared one. */
  error?: string | null
  isRequired?: boolean
  children: (control: FieldControlProps) => ReactNode
}

export function Field({
  label,
  hint,
  error,
  isRequired = false,
  children,
}: FieldProps): ReactElement {
  const id = useId()
  const hintId = `${id}-hint`
  const errorId = `${id}-error`
  const hasHint = hint !== undefined
  const hasError = error !== undefined && error !== null && error !== ''

  const describedBy = [hasHint ? hintId : null, hasError ? errorId : null]
    .filter((value): value is string => value !== null)
    .join(' ')

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-muted">
        {label}
        {isRequired && (
          // The asterisk is decorative — the `required` attribute handed
          // to the control is what a screen reader announces — and the
          // hidden word spells it out for anyone who never learned what
          // an asterisk on a form means.
          <span className="text-status-danger-fg">
            {' '}
            <span aria-hidden="true">*</span>
            <span className="sr-only">(required)</span>
          </span>
        )}
      </label>
      {children({
        id,
        'aria-describedby': describedBy === '' ? undefined : describedBy,
        'aria-invalid': hasError ? true : undefined,
        required: isRequired ? true : undefined,
      })}
      {hasHint && (
        <p id={hintId} className="text-xs text-faint">
          {hint}
        </p>
      )}
      {hasError && (
        <p id={errorId} className="text-xs text-status-danger-fg">
          {error}
        </p>
      )}
    </div>
  )
}
